/**
 * TEST-ONLY settlement back-door reader.
 *
 * A `commerce-gate` invoice minted in a NON-PRODUCTION environment carries a
 * `test_finalize_payment_url` inside `invoice_data`. Opening that URL marks the invoice paid
 * WITHOUT any money moving. It exists so QA can simulate a buyer's SPEI transfer settling
 * instead of making a real interbank payment.
 *
 * This is a SEPARATE channel from {@link ./spei-details}.readSpeiDetails on purpose. The
 * buyer-facing mapping (`SpeiTransferDetails`) must never carry this URL, and a test pins that
 * it doesn't. The URL is DOUBLE-GATED and can only ever surface when BOTH hold:
 *
 *   1. the host environment is not `production`, and
 *   2. the backend actually attached a `test_finalize_payment_url` to the invoice.
 *
 * In production the first gate returns `null` unconditionally, so any QA affordance built on
 * this reader can never render for a real buyer even if a payload somehow carried the field.
 *
 * Like the sibling data modules, this is a pure reader over the RAW commerce-gate invoice (the
 * same object `readSpeiDetails` receives); it takes `unknown` and tolerates any shape.
 */

/** Plain-object guard that also rejects arrays (a JSON array is an object to `typeof`). */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * The host environment, read defensively. This module is also exercised in jest's window-less
 * node environment, where a bare `window` reference throws, so the `typeof` guard is load-bearing
 * rather than cosmetic. `window.config.environment` is the ambient tenant config the host injects.
 */
function hostEnvironment(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window.config as { environment?: string } | undefined)?.environment;
}

/**
 * Extract the test-only settlement URL from a raw commerce-gate invoice, or `null`.
 *
 * Returns `null` when the environment is `production`, when `invoice` is not the expected shape,
 * or when no non-empty `invoice_data.test_finalize_payment_url` string is present.
 */
export function readQaFinalizeUrl(invoice: unknown): string | null {
  // Gate 1: never in production, whatever the payload happens to carry.
  if (hostEnvironment() === 'production') return null;
  // Gate 2: only when the backend actually attached the test back-door.
  if (!isRecord(invoice)) return null;
  const invoiceData = invoice['invoice_data'];
  if (!isRecord(invoiceData)) return null;
  const url = invoiceData['test_finalize_payment_url'];
  return typeof url === 'string' && url.trim() !== '' ? url : null;
}
